import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IChatConversation, IChatConversationScope, IPromptAuditEntry } from 'src/app/model/chat-conversation';
import { ISetupCopilotWizardPrefill, WizardStep } from 'src/app/model/thingie-creation-wizard/wizard-options';
import { IThingie } from 'src/app/model/thingie/thingie';
import { ChatServiceImpl } from './implementation/chat.service-impl';

export type AIProvider = 'openai' | 'anthropic' | 'gemini' | 'ollama';

export type ChartAnalysisRequestedToolMode = 'provider_python';

export type ChatLocalToolGroupId = 'experiment' | 'graph' | 'data' | 'annotations';

export type ChatLocalToolId =
  | 'experiment.create_experiment'
  | 'experiment.duplicate_experiment'
  | 'experiment.auto_assign_devices'
  | 'graph.create_graph'
  | 'graph.modify_graph'
  | 'data.export_experiment_data'
  | 'annotations.annotate_finding';

export interface ChatSelectedTool {
  groupId: ChatLocalToolGroupId;
  toolId: ChatLocalToolId;
}

export interface ChatChartAnalysis {
  csvFilename: string;
  csvContent: string;
  requestedToolMode: ChartAnalysisRequestedToolMode;
}

export interface GraphAgentSeriesSummary {
  seriesKey: string;
  thingieId?: string;
  name: string;
  replicateName?: string;
  path: string;
  taskLabel?: string;
  valueType: string;
  processIndex: number;
  taskIndex: number;
  output: string;
  sampleBundleKey?: string;
}

export interface GraphAgentTabSummary {
  uuid: string;
  label: string;
  seriesCount: number;
  timeReference: string;
}

export interface ExperimentSummaryTask {
  processIndex: number;
  taskIndex: number;
  taskLabel: string;
  deviceType?: string;
}

export interface ExperimentSummaryContext {
  replicateCount: number;
  replicateNames: string[];
  templateName?: string;
  processCount: number;
  taskSummaries: ExperimentSummaryTask[];
  state?: string;
  created?: string;
}

export interface ExperimentComparisonContext {
  targetExperimentName: string;
  current: ExperimentSummaryContext;
  target: ExperimentSummaryContext;
  differences: string[];
}

export interface ExperimentSampleBundleManifest {
  bundleKey: string;
  filename: string;
  replicateThingieId: string;
  replicateName: string;
  seriesCount: number;
  taskCount: number;
  rowCount: number;
}

export interface ExperimentSampleDatasetManifest {
  experimentGroupId: string;
  experimentName: string;
  samplePointsPerSeries: number;
  bundleCount: number;
  totalSeriesCount: number;
  totalRowCount: number;
  bundles: ExperimentSampleBundleManifest[];
  truncated?: boolean;
}

export interface ExperimentSampleCsvBundlePayload {
  bundleKey: string;
  filename: string;
  replicateThingieId: string;
  replicateName: string;
  seriesCount: number;
  taskCount: number;
  rowCount: number;
  csvContent: string;
}

export interface ExperimentSampleDataContext {
  manifest: ExperimentSampleDatasetManifest;
  payloads?: ExperimentSampleCsvBundlePayload[];
}

export type GraphAgentAnnotationCategory = 'user' | 'system';

export interface GraphAgentAnnotationEntry {
  thingieId: string;
  replicateName: string;
  processIndex: number;
  timestamp: string;
  category: GraphAgentAnnotationCategory;
  label: string;
  hasAttachments?: boolean;
}

export interface ExperimentAnnotationsManifest {
  experimentGroupId: string;
  experimentName: string;
  sourceThingieIds: string[];
  replicateCount: number;
  annotationCount: number;
  totalAnnotationCount: number;
  truncated?: boolean;
}

export interface ExperimentAnnotationsContext {
  manifest: ExperimentAnnotationsManifest;
  entries: GraphAgentAnnotationEntry[];
}

export interface GraphAgentContext {
  thingieId: string;
  thingieName: string;
  experimentName: string;
  availableSeries: GraphAgentSeriesSummary[];
  currentTabs: GraphAgentTabSummary[];
  templateName?: string;
  truncated?: boolean;
  experimentSummary?: ExperimentSummaryContext;
  experimentComparison?: ExperimentComparisonContext;
  experimentSampleData?: ExperimentSampleDataContext;
  experimentAnnotations?: ExperimentAnnotationsContext;
}

export type SetupOrganismClass = 'microbial' | 'cell_culture';

export type SetupMeasurementGoal =
  | 'growth'
  | 'do'
  | 'fluorescence'
  | 'feeding'
  | 'offline_biomass'
  | 'otr'
  | 'mixed_monitoring';

export type SetupPendingQuestion = 'organism_class' | 'measurement_goal' | 'replicate_count' | 'project' | 'experiment_name';

export interface SetupTemplateTaskSlot {
  processIdx: number;
  taskIdx: number;
  slotName: string;
  usedFor: string;
  options: { taskTemplateId: string; taskTemplateName: string }[];
}

export interface SetupTemplateRecommendation {
  templateId: string;
  templateName: string;
  organismClass?: SetupOrganismClass;
  matchedGoals: SetupMeasurementGoal[];
  requiredDeviceTypes: string[];
  reasons: string[];
  taskSlots?: SetupTemplateTaskSlot[];
}

export interface SetupProjectOption {
  projectId: string;
  projectTitle: string;
}

export interface SetupExperimentDraft {
  templateId: string;
  templateName: string;
  experimentName: string;
  projectId: string;
  projectTitle: string;
  replicateCount: number;
  organismName?: string;
  organismClass: SetupOrganismClass;
  measurementGoals: SetupMeasurementGoal[];
  requiredDeviceTypes: string[];
}

export interface SetupCopilotBlockingTask {
  processIdx: number;
  taskIdx: number;
  processName?: string;
  taskName: string;
  missingInputLabels: string[];
}

export interface SetupCopilotTaskConfigurationSession {
  templateThingie: IThingie;
  thingies: IThingie[];
  blockingTasks: SetupCopilotBlockingTask[];
  focusedTask?: SetupCopilotBlockingTask;
}

export interface SetupCopilotContext {
  organismName?: string;
  organismClass?: SetupOrganismClass;
  measurementGoals?: SetupMeasurementGoal[];
  requestedTemplateHints?: string[];
  availableProjects?: SetupProjectOption[];
  experimentName?: string;
  replicateCount?: number;
  projectId?: string;
  projectTitle?: string;
  immediateCreateRequested?: boolean;
  recommendationOnly?: boolean;
  perReplicateOverridesRequested?: boolean;
  pendingQuestion?: SetupPendingQuestion;
  recommendations?: SetupTemplateRecommendation[];
  draft?: SetupExperimentDraft;
  taskConfigurationSession?: SetupCopilotTaskConfigurationSession;
}

export interface ChatHiddenContext {
  chartDataCsv?: string;
  chartAnalysis?: ChatChartAnalysis;
  graphAgentContext?: GraphAgentContext;
  setupCopilotContext?: SetupCopilotContext;
  selectedTool?: ChatSelectedTool;
}

export interface ChatAttachmentIndicator {
  kind: 'chart_data';
  label: string;
}

export interface ChatAttachmentSource {
  provider?: AIProvider;
  fileId?: string;
  containerId?: string;
}

export interface ChatCsvAttachment {
  kind: 'csv';
  filename: string;
  mimeType: string;
  csvContent: string;
  source?: ChatAttachmentSource;
}

export type ChatAttachment = ChatCsvAttachment;

export interface GraphActionProposal {
  action: 'createGraphTab' | 'duplicateAndModifyGraphTab' | 'addSeriesToGraph'
    | 'removeSeriesFromGraph' | 'renameGraphTab' | 'selectGraphTab' | 'replaceGraphTabSeries';
  label?: string;
  seriesNames?: string[];
  seriesKeys?: string[];
  timeReference?: 'ABSOLUTE' | 'RELATIVE';
  sourceTabUuid?: string;
  newLabel?: string;
  explanation: string;
}

export interface DuplicateExperimentWizardActionProposal {
  action: 'duplicateAndOpenWizard';
  explanation: string;
  referenceThingieId?: string;
  duplicateReferenceOnly?: boolean;
}

export interface CreateExperimentFromChatActionProposal {
  action: 'createExperimentFromChat';
  explanation: string;
  draft: SetupExperimentDraft;
}

export interface OpenWizardAtStepActionProposal {
  action: 'openWizardAtStep';
  explanation: string;
  initialStep: WizardStep;
  prefill: ISetupCopilotWizardPrefill;
}

export type NavigationViewType = 'thingie_detail' | 'graph_configuration' | 'dashboard';

export interface OpenRelevantViewActionProposal {
  action: 'openRelevantView';
  viewType: NavigationViewType;
  thingieId?: string;
  explanation: string;
}

export interface OpenWizardForTaskConfigurationActionProposal {
  action: 'openWizardForTaskConfiguration';
  thingieId: string;
  explanation: string;
}

export interface PrepareExportBundleActionProposal {
  action: 'prepareExportBundle';
  thingieId: string;
  explanation: string;
}

export interface AutoAssignDevicesActionProposal {
  action: 'autoAssignDevices';
  thingieId: string;
  explanation: string;
}

export interface AnnotateFindingActionProposal {
  action: 'annotateFinding';
  thingieId: string;
  label: string;
  explanation: string;
}

export interface ApplyProjectToSetupPrefillActionProposal {
  action: 'applyProjectToSetupPrefill';
  projectId: string;
  projectTitle: string;
  explanation: string;
}

export interface CreateOrAdjustWizardConfigActionProposal {
  action: 'createOrAdjustWizardConfig';
  experimentName?: string;
  replicateCount?: number;
  organismName?: string;
  organismClass?: SetupOrganismClass;
  measurementGoals?: SetupMeasurementGoal[];
  templateId?: string;
  templateName?: string;
  explanation: string;
}

export interface ResolveTaskTemplateSlotActionProposal {
  action: 'resolveTaskTemplateSlot';
  templateId: string;
  templateName?: string;
  processIdx: number;
  taskIdx: number;
  selectedTaskTemplateId: string;
  selectedTaskTemplateName?: string;
  explanation: string;
}

export type ActionProposal =
  | GraphActionProposal
  | DuplicateExperimentWizardActionProposal
  | CreateExperimentFromChatActionProposal
  | OpenWizardAtStepActionProposal
  | OpenRelevantViewActionProposal
  | OpenWizardForTaskConfigurationActionProposal
  | PrepareExportBundleActionProposal
  | AutoAssignDevicesActionProposal
  | AnnotateFindingActionProposal
  | ApplyProjectToSetupPrefillActionProposal
  | CreateOrAdjustWizardConfigActionProposal
  | ResolveTaskTemplateSlotActionProposal;

export interface ChatMessageContentItem {
  type: 'text' | 'image_url';
  text?: string;
  image_url?: {
    url: string;
  };
}

export type ChatMessageContent = string | ChatMessageContentItem[];

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: ChatMessageContent;
  attachments?: ChatAttachment[];
  hiddenContext?: ChatHiddenContext;
  attachmentIndicators?: ChatAttachmentIndicator[];
  actionProposals?: ActionProposal[];
  reasoning?: string;
  rating?: 'up' | 'down';
}

// Streaming event types
export interface ReasoningDeltaEvent {
  type: 'reasoning_delta';
  delta: string;
}

export interface TextDeltaEvent {
  type: 'text_delta';
  delta: string;
}

export interface StatusEvent {
  type: 'status';
  status: 'web_search_in_progress' | 'web_search_completed' | 'reasoning_started' | 'response_started' | 'analysis_tool_in_progress';
}

export interface ActionProposalEvent {
  type: 'action_proposal';
  actions: ActionProposal[];
}

export interface CompletedEvent {
  type: 'completed';
  message: ChatMessage;
  promptAudit?: IPromptAuditEntry;
}

export interface ErrorEvent {
  type: 'error';
  error: string;
}

export type StreamingEvent = ReasoningDeltaEvent | TextDeltaEvent | StatusEvent | ActionProposalEvent | CompletedEvent | ErrorEvent;

export type ReasoningEffort = 'none' | 'low' | 'medium' | 'high' | 'xhigh';

export interface ChatSettings {
  model: string;
  reasoningEffort: ReasoningEffort;
  promptId: string | null;
  workflowId: string | null;
}

@Injectable({
  providedIn: 'root',
  useClass: ChatServiceImpl
})
export abstract class ChatService {
  public abstract sendMessage(
    message: string,
    images: string[],
    conversationHistory: ChatMessage[],
    provider: AIProvider,
    model?: string,
    hiddenContext?: ChatHiddenContext
  ): Observable<StreamingEvent>;
  public abstract hasApiKey(provider?: AIProvider): Promise<boolean>;
  public abstract getSettings(): Promise<ChatSettings>;

  // Conversation management
  public abstract getConversations(scope?: IChatConversationScope | null): Observable<IChatConversation[]>;
  public abstract getConversation(id: string, scope?: IChatConversationScope | null): Observable<IChatConversation | null>;
  public abstract saveConversation(conversation: IChatConversation): Observable<IChatConversation>;
  public abstract deleteConversation(id: string): Observable<void>;
}
